import {
  getSessionData,
  getVersionedCallbackUrl,
  getUtmParamsFromCookie,
  redirectToWebform,
} from "@repo/shared-utils/common-utils";
import { decryptWebform } from "@repo/shared-utils/crypto";
import { getOrDefaultValue } from "@repo/shared-utils/insert-params";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import {
  mapMyIndia,
  solarMaster,
  solarInsert,
  solarRead,
  type IMapMyIndiaRequest,
  type IMapMyIndiaResponse,
  type ISolarInsertApiRequest,
  type ISolarReadLoanDetails,
} from "@repo/connection-manager";
import type { SSORedirectHandler } from "../types/SSO.types";
import { SOLAR_APPLICATION_ERROR_EVENT } from "@repo/sso/constants";

type SolarFormInsertData = Partial<ISolarInsertApiRequest> & {
  cta?: string;
  sourceUrl?: string;
  retryCount?: string;
};

type DecryptedReqData = {
  ssoRequest?: {
    form?: {
      applicationID?: string;
      mobileNo?: string;
    };
  };
};

const SOLAR_FORM_HANDLER_LOG = "[SolarSSO][FormHandler]";

const pad2 = (value: number): string => value.toString().padStart(2, "0");

const formatRequestTime = (date: Date): string => {
  return [
    `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`,
    `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`,
  ].join(" ");
};

const MOD_BIAS_FLOOR = 4294967000;

const generateMmiRequestId = (): string => {
  const array = new Uint32Array(1);
  do {
    crypto.getRandomValues(array);
  } while (array[0]! >= MOD_BIAS_FLOOR);
  const random = (array[0]! % 1000).toString().padStart(3, "0");
  const suffix = `${Date.now()}${random}`.slice(-11);

  return `WHLU${suffix}`;
};

const normalizeAddress = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const getInstallationAddress = (solarFormInsertData: SolarFormInsertData): string => {
  const appDetails = solarFormInsertData.appDtls?.[0];
  const installationAddress = appDetails?.kycDtls?.find(
    (address) => address?.address_type === "Installation Residence"
  );

  const candidates = [
    installationAddress?.address_line_1,
    installationAddress?.address_line_2,
    installationAddress?.address_line_3,
    solarFormInsertData.city,
    installationAddress?.address_pin,
    solarFormInsertData.pincode,
  ];

  return candidates.map(normalizeAddress).find(Boolean) ?? "";
};

const getInstallationPincode = (solarFormInsertData: SolarFormInsertData): string => {
  const appDetails = solarFormInsertData.appDtls?.[0];
  const installationAddress = appDetails?.kycDtls?.find(
    (address) => address?.address_type === "Installation Residence"
  );

  const installationPin = normalizeAddress(installationAddress?.address_pin);

  return installationPin || normalizeAddress(solarFormInsertData.pincode) || "";
};

const buildMmiPayload = (address: string): IMapMyIndiaRequest => {
  const now = new Date();

  return {
    requestId: generateMmiRequestId(),
    requestTime: formatRequestTime(now),
    address,
  };
};

const parseBranchCodeFromMmiData = (rawData: unknown): string => {
  if (typeof rawData === "string") {
    const trimmed = rawData.trim();
    if (!trimmed) {
      return "";
    }

    try {
      const parsed = JSON.parse(trimmed) as { branchCode?: string | number };
      const parsedBranchCode = `${parsed?.branchCode ?? ""}`.trim();
      return parsedBranchCode || trimmed;
    } catch {
      return trimmed;
    }
  }

  if (typeof rawData === "object" && rawData !== null) {
    const data = rawData as { branchCode?: string | number };
    return `${data.branchCode ?? ""}`.trim();
  }

  return "";
};

const getMmiBranchCode = (mmiResponse: IMapMyIndiaResponse): string => {
  return parseBranchCodeFromMmiData(mmiResponse?.responseBodyOP?.data);
};

const getReqDataForm = async (): Promise<{ applicationID?: string; mobileNo?: string }> => {
  const reqData = new URLSearchParams(window.location.search).get("reqData");
  if (!reqData) {
    return {};
  }

  try {
    const decrypted = await decryptWebform(decodeURIComponent(reqData));
    const parsed = JSON.parse(decrypted) as DecryptedReqData;

    return {
      applicationID: parsed?.ssoRequest?.form?.applicationID,
      mobileNo: parsed?.ssoRequest?.form?.mobileNo,
    };
  } catch (error) {
    console.error(`${SOLAR_FORM_HANDLER_LOG} failed to parse reqData`, error);
    return {};
  }
};

const getPrimaryLoan = (readResponse: { solarLoanDetailsList: ISolarReadLoanDetails[] | null }) =>
  readResponse?.solarLoanDetailsList?.[0];

export const solarFormHandler: SSORedirectHandler = async (data) => {
  console.log(`${SOLAR_FORM_HANDLER_LOG} invoked`, {
    module: data.mapping?.ssoModule,
    href: data.mapping?.fullUrl,
  });

  const { mobileNo: sessionMobileNo, custType } = await getSessionData();
  const solarFormInsertData = (data.data?.solarFormInsert ?? {}) as SolarFormInsertData;
  const { applicationID, mobileNo: reqDataMobileNo } = await getReqDataForm();
  const mobileNo = reqDataMobileNo || sessionMobileNo;
  const callbackUrl = await getVersionedCallbackUrl(data.mapping);

  if (!applicationID || !mobileNo) {
    console.log(`${SOLAR_FORM_HANDLER_LOG} missing reqData identifiers`, {
      applicationID,
      mobileNoPresent: Boolean(mobileNo),
    });

    console.warn(`${SOLAR_FORM_HANDLER_LOG} missing applicationID/mobileNo; redirecting directly`);
    await redirectToWebform(callbackUrl.toString(), {
      applicationID,
      mobileNo,
    });
    return;
  }

  console.info(`${SOLAR_FORM_HANDLER_LOG} start`, {
    applicationID,
    mobileNoPresent: Boolean(mobileNo),
    mappingModule: data.mapping?.ssoModule,
  });

  const readResponse = await solarRead({
    id: applicationID,
    mobileNo,
  });
  const loan = getPrimaryLoan(readResponse);
  const utm = getUtmParamsFromCookie();
  const mmiAddress = getInstallationAddress(solarFormInsertData);
  let productCode = `${loan?.prodName ?? "SRL"}`.trim();

  if (mmiAddress) {
    const mmiPayload = buildMmiPayload(mmiAddress);

    try {
      const mmiResponse = await mapMyIndia(mmiPayload);
      const branchCode = getMmiBranchCode(mmiResponse);

      console.info(`${SOLAR_FORM_HANDLER_LOG} mapMyIndia complete`, {
        requestId: mmiPayload.requestId,
        statusCode: mmiResponse?.statusCode,
        branchData: mmiResponse?.responseBodyOP?.data,
        branchCode,
        responseStatus: mmiResponse?.responseBodyOP?.status,
      });

      if (branchCode) {
        const solarMasterResponse = await solarMaster({ branchCode });
        const resolvedProductCode = `${solarMasterResponse?.solarMaster?.productName ?? ""}`.trim();

        if (resolvedProductCode) {
          productCode = resolvedProductCode;
        }

        console.info(`${SOLAR_FORM_HANDLER_LOG} solarMaster complete`, {
          branchCode,
          statusCode: solarMasterResponse?.statusCode,
          productCode,
        });
      }
    } catch (error) {
      console.warn(`${SOLAR_FORM_HANDLER_LOG} mapMyIndia failed`, {
        requestId: mmiPayload.requestId,
        error,
      });
    }
  } else {
    console.info(`${SOLAR_FORM_HANDLER_LOG} mapMyIndia skipped due to missing address`);
  }

  let insertResponse;
  try {
    insertResponse = await solarInsert({
      ...solarFormInsertData,
      id: applicationID,
      loanAmt: loan?.loanAmt,
      mobileNo,
      custType,
      journeyName: loan?.journeyName ?? "SRL_NTB",
      eventCode: "WHEELS_CONVERT",
      eventName: "PERSONAL_DETAILS",
      prodName: productCode ?? loan?.prodName ?? "SRL",
      appVersion: "v1",
      saveForLaterFlag: true,
      pwaVersion: "5.1.9",
      sourceSystem: isOpenInApp() ? "3in1App" : "3in1Web",
      sourceChannel: "3in1 App",
      srcChannel: "3in1",
      apiVersion: "P.11.1.4",
      utmSource: getOrDefaultValue(utm?.utmSource),
      utmContent: getOrDefaultValue(utm?.utmContent),
      utmCampaign: getOrDefaultValue(utm?.utmCampaign),
      utmMedium: getOrDefaultValue(utm?.utmMedium),
      offerId: loan?.offerId,
      offerName: loan?.offerName,
      offerAmt: loan?.offerPreApprovedAmount,
    });
  } catch (error) {
    console.error(`${SOLAR_FORM_HANDLER_LOG} solarInsert failed`, error);
    window.dispatchEvent(
      new CustomEvent(SOLAR_APPLICATION_ERROR_EVENT, { detail: { source: "solarFormHandler" } })
    );
    return;
  }
  //for removing the exit popup state
  if (!isOpenInApp()) {
    window.history.go(-1);
  }
  await redirectToWebform(callbackUrl.toString(), {
    applicationID: insertResponse?.id ?? applicationID,
    mobileNo,
    amount: loan?.loanAmt,
    pincode: getInstallationPincode(solarFormInsertData),
    electricityBoard: solarFormInsertData.electricityBoard,
    isVc: true,
  });

  console.info(`${SOLAR_FORM_HANDLER_LOG} complete`, {
    applicationID: insertResponse?.id ?? applicationID,
  });
};
